import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Banner from "./Banner";
import { getViewByViewable } from "../../selectors/viewSelectors";
import { clickView, createView } from "../../processes/viewProcesses";
import { checkEventSource, createEventSource } from "../../processes/eventSourceProcesses";
import { fetchBanners } from "../../processes/bannerProcesses";
import useOnClick from "../../utils/useOnClick";

function BannerContainer(props) {
  const { dispatch, banner, view, viewsLoading } = props;

  const isLightbox = banner?.type == "lightbox";
  const url = !isLightbox && banner?.url;
  const linkTarget = banner?.external ? "_blank" : "_self";

  const bannerRef = useRef(null);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState(null);
  const [eventCreated, setEventCreated] = useState(false);
  const [eventSuccess, setEventSuccess] = useState(false);
  const checkedEventRef = useRef(false);

  const toggleLightbox = useCallback(() => {
    if (!isLightbox) return;
    setOpenLightbox(!openLightbox);
  }, [isLightbox, openLightbox]);

  useEffect(() => {
    if (!viewsLoading && !view) {
      createView(dispatch, {
        viewable_id: banner.id,
        viewable_type: "Banner",
      });
    }
  }, [dispatch, viewsLoading, banner, view]);

  const buildEventMetadata = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_content: urlParams.get('utm_content') || null,
      referrer: document.referrer || null,
      page_source: banner?.displayLocation == 'home_page' ? 'lms_home_banner' : 'lms_store_banner'
    };
  }, [banner?.displayLocation]);

  const bannerClickHandler = useCallback(() => {
    if (!openLightbox) toggleLightbox();
    clickView(dispatch, view.id).finally(() => {
      if (!isLightbox) open(url, linkTarget);
    });
  }, [
    dispatch,
    view,
    isLightbox,
    toggleLightbox,
    openLightbox,
    url,
    linkTarget,
  ]);

  // Check if event source already exists when modal opens
  useEffect(() => {
    if (openLightbox && banner?.requiresEventCreation && !checkedEventRef.current && !eventCreated) {
      checkedEventRef.current = true;

      checkEventSource(dispatch, banner.id)
        .then((response) => {
          if (response.exists) {
            setEventCreated(true);
          }
        })
        .catch(() => {
          // Ignore errors on initial check
        });
    }
  }, [openLightbox, banner, eventCreated, dispatch]);

  const handleEventCreate = useCallback((bannerId) => {
    setEventLoading(true);
    setEventError(null);

    const metadata = buildEventMetadata();

    createEventSource(dispatch, bannerId, metadata)
      .then((response) => {
        setEventLoading(false);
        setEventCreated(true);

        // Only show success message if it's a new event (201)
        if (response.isNew) {
          setEventSuccess(true);
          // Show success message for 3 seconds before closing
          setTimeout(() => {
            setEventSuccess(false);
            toggleLightbox();
            // Backend now hides banners with a created event; refetch so it disappears immediately
            fetchBanners(dispatch);
          }, 3000);
        }
      })
      .catch((error) => {
        setEventLoading(false);
        setEventError("Erro ao realizar inscrição. Tente novamente.");
        console.error("Event source creation error:", error);
      });
  }, [dispatch, buildEventMetadata, toggleLightbox]);

  const handleEventCreateAndLink = useCallback((bannerId, url) => {
    setEventLoading(true);
    setEventError(null);

    const metadata = buildEventMetadata();

    createEventSource(dispatch, bannerId, metadata)
      .then((response) => {
        setEventLoading(false);
        setEventCreated(true);

        // Only show success message if it's a new event (201)
        if (response.isNew) {
          setEventSuccess(true);
        }

        window.open(url, linkTarget);

        // Show success message for 3 seconds before closing
        setTimeout(() => {
          setEventSuccess(false);
          toggleLightbox();
          // Backend now hides banners with a created event; refetch so it disappears immediately
          fetchBanners(dispatch);
        }, 3000);
      })
      .catch((error) => {
        setEventLoading(false);
        setEventError("Erro ao realizar inscrição. Tente novamente.");
        console.error("Event source creation error:", error);
      });
  }, [dispatch, buildEventMetadata, linkTarget, toggleLightbox]);

  useOnClick(bannerClickHandler, bannerRef);

  return (
    <Banner
      isLightbox={isLightbox}
      openLightbox={openLightbox}
      toggleLightbox={toggleLightbox}
      linkTarget={linkTarget}
      onEventCreate={handleEventCreate}
      onEventCreateAndLink={handleEventCreateAndLink}
      eventLoading={eventLoading}
      eventError={eventError}
      eventCreated={eventCreated}
      eventSuccess={eventSuccess}
      ref={bannerRef}
      {...props}
    />
  );
}

function mapStateToProps(state, { banner }) {
  return {
    viewsLoading: state.viewList.loading,
    view: getViewByViewable(state, {
      viewableType: "Banner",
      viewableId: banner.id,
    }),
  };
}

export default connect(mapStateToProps)(BannerContainer);
